import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { assertAddress } from '@soundxyz/utils/address';
import { chainIds, ContractMethod } from '@soundxyz/utils/const';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { artistContractV5Abi } from '@abi/artistContractV5';
import { styled } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import { Modal } from '@components/main-components/modals/Modal';
import { TextBlurb } from '@components/modals/shared/styles';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext } from '@context/modal';
import { ToastContext } from '@context/toast';
import { Transaction } from '@context/transaction';
import { getTransactionResponse } from '@context/transaction/TransactionStatusStore';
import { useQuery, useReactQuery } from '@graphql/reactQuery';
import { CollectReleaseDocument } from '@graphql/reactQuery';
import { useConnectedWallet } from '@hooks/useConnectedWallet';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { chainFromId } from '@utils/chain';
import { AVAILABLE_CHAIN_IDS, EMPTY_SIGNATURE } from '@utils/constants';
import { formatWeiToEthForDisplay } from '@utils/helpers';
import { DataTestId } from '@utils/test';
import { COMMENT_MODAL_BLURB } from '../Comment/constants';
import { ReleaseDetails } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query CollectRelease($releaseId: UUID!) {
    release(id: $releaseId) {
      id
      contractAddress
      title
      editionId
      price
      quantity
      quantityUpperBound
      finalQuantity
      numSold
      coverImage {
        id
        url
      }
      ...ReleaseDetails
    }
  }
`);

function useBlockNumber() {
  const { provider } = useConnectedAuth();

  const { data: lastBlockTimestamp } = useReactQuery({
    queryKey: ['blocknumber'],
    async queryFn() {
      const chainProvider = provider(AVAILABLE_CHAIN_IDS.eth);

      const blockNumber = await chainProvider.getBlockNumber();

      const block = await chainProvider.getBlock({ blockNumber });

      return block.timestamp;
    },
  });

  return {
    lastBlockTimestamp,
  };
}

export function BuyArtistContractModal({ releaseId }: { releaseId: string }) {
  const { closeCancelModal } = ModalContext.useContainer();

  const [isBuyPending, setIsBuyPending] = React.useState(false);

  const { data } = useQuery(CollectReleaseDocument, {
    variables: {
      releaseId,
    },
    staleTime: 0,
  });

  const release = data?.data.release;

  const { authUser } = useAuth();
  const { expectMatchingChain, getSigner, getDeepLink, provider } = useConnectedAuth();

  const { handleTransaction } = Transaction.useContainer();
  const { openToast } = ToastContext.useContainer();
  const logError = useLogError();
  const logInfo = useLogInfo();
  const { lastBlockTimestamp } = useBlockNumber();

  const canCollectSafely = !!(authUser && lastBlockTimestamp);

  const handleBuy = useConnectedWallet({
    async onAuth() {
      if (!editionId || !contractAddress) return;

      const chain = expectMatchingChain(AVAILABLE_CHAIN_IDS.eth);

      if (chain.stop) return;

      setIsBuyPending(true);

      try {
        if (chain.switching) await chain.switching;
      } catch (error) {
        openToast({
          text: 'Please switch to ethereum mainnet',
          variant: 'error',
        });
        setIsBuyPending(false);
        return;
      }

      const signer = await getSigner(AVAILABLE_CHAIN_IDS.eth);

      const chainProvider = provider(AVAILABLE_CHAIN_IDS.eth);

      assertAddress(contractAddress);

      const [
        _fundingRecipient,
        price,
        numSold,
        quantity,
        _royaltyBPS,
        _startTime,
        _endTime,
        permissionedQuantityContract,
      ] = await chainProvider.readContract({
        abi: artistContractV5Abi,
        address: contractAddress,
        functionName: 'editions',
        args: [BigInt(editionId)],
      });

      // pre V4 contracts will have a null permissionedQuantity
      const permissionedQuantity = permissionedQuantityContract ?? 0;

      if (numSold === Math.max(quantity, permissionedQuantity)) {
        openToast({
          text: 'This edition is sold out!',
          variant: 'error',
        });
        return;
      }

      /**
       * This is critical for a better mobile UX.
       * We have to set before the actual transaction is sent.
       * This way it can open the user's wallet app and make it a seamless flow
       */
      const deepLink = getDeepLink();
      if (deepLink) {
        window.location.href = deepLink;
      }

      const transactionFn = async () => {
        const txHash = await signer.writeContract({
          abi: artistContractV5Abi,
          address: contractAddress,
          functionName: 'buyEdition',
          args: [BigInt(editionId), EMPTY_SIGNATURE, 0n],
          value: price,
          chain: chainFromId(chainIds.MAINNET),
          gas: 200_000n,
        });

        return getTransactionResponse({
          hash: txHash,
          chainId: AVAILABLE_CHAIN_IDS.eth,
          provider: chainProvider,
        });
      };

      logInfo({
        action: MINTING_INFO_ACTIONS.ARTIST_CONTRACT_MINT,
        message: 'Minting from a legacy artist contract ',
        data: {
          releaseId,
          editionId,
        },
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
      });
      try {
        await handleTransaction({
          contractMethod: ContractMethod.ARTIST__BUY_EDITION,
          transactionFn,
          releaseId,
          chainId: AVAILABLE_CHAIN_IDS.eth,
          openProcessingModal: true,
        });
      } catch (error) {
        logError({
          action: MINTING_ERROR_ACTIONS.ARTIST_CONTRACT_MINT_ERROR,
          error,
          message: 'Error minting an artist contract NFT',
          closeProcessingModal: true,
          pillar: PILLARS.MINTING,
          indexedTags: {
            releaseId,
            editionId,
          },
          errorType: ERROR_TYPE.ARTIST_CONTRACT_MINT,
          isArtistOnlyPage: false,
          level: 'fatal',
        });
      } finally {
        setIsBuyPending(false);
      }
    },
  });

  // Ensures there is no chance of the buyer sending a tx with 0 ETH
  if (!release || !release.price) {
    return null;
  }

  const { contractAddress, editionId, finalQuantity, quantityUpperBound, numSold } = release;
  const currentMaxQuantity = finalQuantity || quantityUpperBound;
  const remaining = currentMaxQuantity - numSold;

  const body = (
    <ModalBody data-testid={DataTestId.BUY_AND_COMMENT_MODAL_CONTAINER}>
      {release && <ReleaseDetails releaseData={release} selectedMintPanel="LIMITED" />}
      <NFTInfoContainer>
        <NFTInfo>
          {remaining}/{currentMaxQuantity}
        </NFTInfo>
        <NFTInfo>{formatWeiToEthForDisplay({ weiValue: release.price })} ETH</NFTInfo>
      </NFTInfoContainer>
      <TextBlurb legacy>{COMMENT_MODAL_BLURB}</TextBlurb>
      <Wrapper>
        <InputLabel>ETH Amount</InputLabel>
        <Input>{formatWeiToEthForDisplay({ weiValue: release.price })}&nbsp;ETH</Input>
      </Wrapper>
      <div>
        <Button
          data-testid={DataTestId.BUY_MODAL_COLLECT_BUTTON}
          label={
            isBuyPending
              ? 'Collecting...'
              : canCollectSafely
              ? 'Collect'
              : 'Collect enabled soon...'
          }
          variant="fuchsia"
          onClick={() => handleBuy()}
          disabled={isBuyPending || !canCollectSafely}
          fullWidth
        />
      </div>
    </ModalBody>
  );
  return (
    <Modal
      body={body}
      headerText="Checkout"
      headerStyles={{
        padding: 16,
        '@tablet': {
          padding: 24,
        },
      }}
      containerStyles={{
        padding: 0,
      }}
      bodyStyles={{ marginTop: 0 }}
      closeModal={closeCancelModal}
    />
  );
}

const Input = styled('div', {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '$neutral50',
  border: '1px solid $neutral300',
  borderRadius: 6,
  height: 40,
  padding: '10px 12px',
  base: 'L',
  color: '$neutral500',
});

const NFTInfoContainer = styled('div', {
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: 8,
});

const NFTInfo = styled('div', {
  backgroundColor: '#ECF7F4',
  color: '#1F9E7F',
  fontSize: 12,
  fontStyle: 'semibold',
  borderRadius: 10,
  paddingX: 10,
  paddingY: 3,
});

const ModalBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: '0 16px 16px',
  gap: 16,
  '@tablet': {
    gap: 24,
    padding: '0 24px 24px',
  },
});

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const InputLabel = styled('p', {
  base: 'M',
  fontWeight: 600,

  '@tablet': {
    base: 'L',
  },
});
